import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { privilegeReviewApi } from '../../../api';

const REVIEWS_KEY = ['privilege-reviews'];
const UPCOMING_KEY = ['privilege-reviews', 'upcoming'];
const STATS_KEY = ['privilege-reviews', 'stats'];
const ROSTER_KEY = ['privilege-reviews', 'roster'];
const REMEDIATIONS_KEY = ['privilege-reviews', 'remediations'];

/**
 * Fetches a paginated list of privilege reviews.
 * @param {{ page?: number, per_page?: number, status?: string }} params
 */
export function useReviews(params = {}) {
  const query = useQuery({
    queryKey: [...REVIEWS_KEY, params],
    queryFn: async () => {
      const result = await privilegeReviewApi.listReviews(params);
      return result.data;
    },
  });

  return {
    reviews: query.data?.data?.reviews ?? [],
    total: query.data?.data?.total ?? 0,
    pages: query.data?.data?.pages ?? 1,
    currentPage: query.data?.data?.current_page ?? 1,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Fetches a single privilege review by ID.
 * @param {number} id
 */
export function useReview(id) {
  const query = useQuery({
    queryKey: [...REVIEWS_KEY, id],
    queryFn: async () => {
      const result = await privilegeReviewApi.getReview(id);
      return result.data;
    },
    enabled: !!id,
  });

  return {
    review: query.data?.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}

/**
 * Mutation to create a new privilege review.
 */
export function useCreateReview() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('privilegeReview');

  return useMutation({
    mutationFn: (data) => privilegeReviewApi.createReview(data),
    onSuccess: () => {
      toast.success(t('reviewCreated'));
      queryClient.invalidateQueries({ queryKey: REVIEWS_KEY });
      queryClient.invalidateQueries({ queryKey: UPCOMING_KEY });
      queryClient.invalidateQueries({ queryKey: STATS_KEY });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || error?.message || t('errorCreating'));
    },
  });
}

/**
 * Mutation to update an existing privilege review.
 */
export function useUpdateReview() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('privilegeReview');

  return useMutation({
    mutationFn: ({ id, data }) => privilegeReviewApi.updateReview(id, data),
    onSuccess: () => {
      toast.success(t('reviewUpdated'));
      queryClient.invalidateQueries({ queryKey: REVIEWS_KEY });
      queryClient.invalidateQueries({ queryKey: UPCOMING_KEY });
      queryClient.invalidateQueries({ queryKey: STATS_KEY });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || error?.message || t('errorUpdating'));
    },
  });
}

/**
 * Fetches KPI stats for the ribbon (total, pending, completed, overdue).
 */
export function useReviewStats() {
  const query = useQuery({
    queryKey: STATS_KEY,
    queryFn: async () => {
      const result = await privilegeReviewApi.getStats();
      return result.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  return {
    stats: query.data?.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}

/**
 * Fetches the upcoming review status — whether a review is due soon.
 */
export function useUpcomingReview() {
  const query = useQuery({
    queryKey: UPCOMING_KEY,
    queryFn: async () => {
      const result = await privilegeReviewApi.getUpcoming();
      return result.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    upcoming: query.data?.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}

/**
 * Fetches the privileged-access roster (H1 grounding) — the active population a
 * new review must attest. Enabled by default; callers pass `enabled: false` to
 * skip the fetch (e.g. when editing an existing review).
 * @param {{ enabled?: boolean }} [options]
 */
export function useRoster({ enabled = true } = {}) {
  const query = useQuery({
    queryKey: ROSTER_KEY,
    queryFn: async () => {
      const result = await privilegeReviewApi.getRoster();
      return result.data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  return {
    roster: query.data?.data?.roster ?? [],
    total: query.data?.data?.total ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Fetches org-level remediations, optionally filtered by status.
 * @param {{ status?: string }} [params]
 */
export function useRemediations(params = {}) {
  const query = useQuery({
    queryKey: [...REMEDIATIONS_KEY, params],
    queryFn: async () => {
      const result = await privilegeReviewApi.getRemediations(params);
      return result.data;
    },
  });

  return {
    remediations: query.data?.data?.remediations ?? [],
    total: query.data?.data?.total ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/** Resolve a human-readable error message from the httpClient error envelope. */
function errorMessage(error, fallback) {
  return error?.response?.data?.error || error?.message || fallback;
}

/**
 * Mutation to enact a remediation — moves the subject to `target_role_id`.
 * Requires the actor to hold both `manage_privilege_reviews` and `edit_users`
 * (Super Admin in practice); the backend returns 403 otherwise, surfaced as a
 * clear toast.
 */
export function useEnactRemediation() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('privilegeReview');

  return useMutation({
    mutationFn: ({ id, target_role_id }) =>
      privilegeReviewApi.enactRemediation(id, { target_role_id }),
    onSuccess: () => {
      toast.success(t('remediation.enacted'));
      queryClient.invalidateQueries({ queryKey: REVIEWS_KEY });
    },
    onError: (error) => {
      if (error?.status === 403) {
        toast.error(t('remediation.enactForbidden'));
        return;
      }
      toast.error(errorMessage(error, t('remediation.errorEnacting')));
    },
  });
}

/**
 * Mutation to close a remediation (attested done out-of-band). Requires a note.
 */
export function useCloseRemediation() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('privilegeReview');

  return useMutation({
    mutationFn: ({ id, closure_note }) =>
      privilegeReviewApi.closeRemediation(id, { closure_note }),
    onSuccess: () => {
      toast.success(t('remediation.closed'));
      queryClient.invalidateQueries({ queryKey: REVIEWS_KEY });
    },
    onError: (error) => {
      toast.error(errorMessage(error, t('remediation.errorClosing')));
    },
  });
}

/**
 * Mutation to cancel a remediation (finding withdrawn). Requires a note.
 */
export function useCancelRemediation() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('privilegeReview');

  return useMutation({
    mutationFn: ({ id, closure_note }) =>
      privilegeReviewApi.cancelRemediation(id, { closure_note }),
    onSuccess: () => {
      toast.success(t('remediation.cancelled'));
      queryClient.invalidateQueries({ queryKey: REVIEWS_KEY });
    },
    onError: (error) => {
      toast.error(errorMessage(error, t('remediation.errorCancelling')));
    },
  });
}
